import { Directive, HostListener, Input } from '@angular/core';
import moment from 'moment';
import type { SheetData } from 'write-excel-file/browser';
import { IExpert } from '../../../shared/models/expert.interface';
import { TimezonePreferenceService } from '../../../shared/services/timezone-preference.service';
import {
  IExpertPhases,
  PortalPhases,
} from '../../../shared/state/expert.service';
import { WorksheetService } from '../services/worksheet.service';

@Directive({
  selector: 'button[appExperts]',
  standalone: false,
})
export class ExpertSummaryDownloadDirective {
  @Input('appExperts') experts: IExpertPhases;
  @Input() opportunityName: string;

  constructor(
    private worksheetService: WorksheetService,
    private timezonePreferenceService: TimezonePreferenceService
  ) {}

  @HostListener('click')
  downloadProgressCsv(): void {
    this.downloadProgressCsvImpl();
  }

  private downloadProgressCsvImpl(): void {
    const formattedOpportunityName = this.opportunityName
      .replace(/[?:\\\/[\]*]/g, '')
      .trim()
      .slice(0, 31);

    const data = this.formatWorksheet(this.experts);

    this.worksheetService.save(
      [
        {
          sheet: formattedOpportunityName,
          data,
          columns: data[0].map(() => ({ width: 23 })),
        },
      ],
      'export'
    );
  }

  private formatWorksheet(experts: Record<PortalPhases, IExpert[]>): SheetData {
    const headers = [
      'Expert initials',
      'Geography',
      'Profile Type',
      'Bio',
      'Screener',
      'Phase',
      'Call time',
      'Comments',
      'Last updated',
    ];

    const rows = Object.entries(experts)
      .map(([k, v]) => v.map((e) => ({ ...e, k })))
      .flat()
      .map((e: IExpert & { k: PortalPhases }) => [
        `${e.firstName[0]} ${e.lastName[0]}`,
        e.geographicTarget,
        e.profileType,
        this.stripHTML(e.bio || ''),
        this.stripHTML(e.screenerResponse || ''),
        this.convertPortalPhase(e.k),
        moment(e.callTime)
          .tz(this.timezonePreferenceService.selectedTimezone())
          .format('DD-MM-YYYY HH:mm'),
        e.clientNotes,
        moment(e.lastUpdated * 1000)
          .tz(this.timezonePreferenceService.selectedTimezone())
          .format('DD-MM-YYYY HH:mm'),
      ]);

    return [headers, ...rows];
  }

  private convertPortalPhase(portalPhase: PortalPhases): string {
    switch (portalPhase) {
      case 'newExperts':
        return 'New profile';
      case 'acceptedExperts':
        return 'Upcoming connection';
      case 'completedConnections':
        return 'Completed connection';
      case 'declinedExperts':
        return 'Declined expert';
      default:
        return 'New profile';
    }
  }

  private stripHTML(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }
}
